import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import SomethingHappened from '@/components/SomethingHappened';
import { useAuth, usePermissions } from '@/contexts/auth';
import { useCageTimezone } from '@/contexts/cage';
import { usePenCohortFishGroup } from '@/contexts/pen-cohort-context';
import { useCommitImageFeedback } from '@/domain/imageFeedback';
import { fullSizeFinImageMapper, fullSizeImageMapper, useGetWelfareImages } from '@/domain/images';
import { useImageState } from '@/hooks/image-state';
import CageImagePageContent from '@/routes/Client/Cage/pages/CageDashboard/CageImages/CageImagePageContent';
import type { CagePageInterface } from '@/routes/Client/Cage/utils/cage-pages-interface';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

export default function CageImagePageContainer(props: CagePageInterface) {
    const { cageId } = props;

    const timezone = useCageTimezone();
    const { user } = useAuth();
    const { isOptoScaleAdmin } = usePermissions();
    const { t } = useTranslation();

    // The images of the group the page is about, not every group the pen has held.
    const imagesFishGroup = usePenCohortFishGroup();
    const detections = useGetWelfareImages(
        cageId ?? 0,
        { type: MERGED_WELFARE_IMAGES_TYPE },
        { fishGroup: imagesFishGroup }
    );
    const imageMapper = isOptoScaleAdmin ? fullSizeImageMapper : fullSizeFinImageMapper;

    const mapper = useMemo(() => imageMapper(cageId), [cageId, imageMapper]);

    // Sorted on a copy: sorting `detections.data` itself would reorder the query cache.
    const newestFirst = useMemo(
        () =>
            detections.data
                ? [...detections.data].sort(
                      (a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime()
                  )
                : undefined,
        [detections.data]
    );

    const detectionImageState = useImageState({
        loading: detections.isLoading && !detections.error,
        data: newestFirst,
        mapper,
        queryParam: 'imageId',
        timezone,
    });

    const onCommitImageFeedback = useCommitImageFeedback({ cageId });

    // The page's title goes with the content, so the error state stands in as its h1.
    if (detections.error) {
        return <SomethingHappened headingLevel="h1" {...retryFailedQueries(detections)} />;
    }

    return (
        <CageImagePageContent
            detectionImageState={detectionImageState}
            isOptoScaleAdmin={isOptoScaleAdmin}
            onCommitFalseDetection={onCommitImageFeedback}
        />
    );
}
